/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Seed Assets Inventory & 1-Click Syncer DTOs (Task 11.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

export const SeedAssetStatusSchema = z
  .enum(['SYNCED', 'MISSING', 'OUTDATED'])
  .openapi({ example: 'SYNCED', description: 'Current R2 object synchronization status' });

export type SeedAssetStatus = z.infer<typeof SeedAssetStatusSchema>;

export const SeedAssetCategorySchema = z
  .enum(['voice', 'bgm', 'sfx', 'brand', 'avatar', 'template', 'audio'])
  .openapi({ example: 'voice', description: 'Logical category of seed resource' });

export type SeedAssetCategory = z.infer<typeof SeedAssetCategorySchema>;

export const SeedAssetItemSchema = z
  .object({
    key: z.string().openapi({ example: 'samples/voices/alloy.mp3', description: 'Relative R2 storage key' }),
    category: SeedAssetCategorySchema,
    contentType: z.string().openapi({ example: 'audio/mpeg', description: 'IANA MIME Content Type' }),
    description: z.string().openapi({ example: 'OpenAI Alloy - Versatile, balanced and neutral voice sample' }),
    status: SeedAssetStatusSchema,
    sizeBytes: z.number().openapi({ example: 45056, description: 'File size in bytes' }),
    etag: z.string().optional().openapi({ example: 'a1b2c3d4e5f6', description: 'R2 entity tag' }),
    publicUrl: z.string().openapi({ example: 'https://media.sophiaplatform.com/samples/voices/alloy.mp3' }),
    uploadedAt: z.string().optional().openapi({ example: '2026-10-26T15:00:00Z' }),
  })
  .openapi('SeedAssetItem');

export type SeedAssetItem = z.infer<typeof SeedAssetItemSchema>;

export const SeedAssetSummarySchema = z
  .object({
    total: z.number().openapi({ example: 47 }),
    synced: z.number().openapi({ example: 47 }),
    missing: z.number().openapi({ example: 0 }),
    outdated: z.number().openapi({ example: 0 }),
  })
  .openapi('SeedAssetSummary');

export type SeedAssetSummary = z.infer<typeof SeedAssetSummarySchema>;

export const SeedAssetListResponseSchema = z
  .object({
    items: z.array(SeedAssetItemSchema),
    summary: SeedAssetSummarySchema,
  })
  .openapi('SeedAssetListResponse');

export type SeedAssetListResponse = z.infer<typeof SeedAssetListResponseSchema>;

export const SeedAssetSyncResponseSchema = z
  .object({
    syncedCount: z.number().openapi({ example: 47 }),
    skippedCount: z.number().openapi({ example: 0 }),
    total: z.number().openapi({ example: 47 }),
    timestamp: z.string().openapi({ example: '2026-10-26T15:00:00Z' }),
    summary: SeedAssetSummarySchema,
    items: z.array(SeedAssetItemSchema),
  })
  .openapi('SeedAssetSyncResponse');

export type SeedAssetSyncResponse = z.infer<typeof SeedAssetSyncResponseSchema>;

export const SeedAssetVerifyResponseSchema = z
  .object({
    verifiedAt: z.string().openapi({ example: '2026-10-26T15:00:00Z' }),
    total: z.number().openapi({ example: 47 }),
    synced: z.number().openapi({ example: 47 }),
    missing: z.number().openapi({ example: 0 }),
    outdated: z.number().openapi({ example: 0 }),
    allSynced: z.boolean().openapi({ example: true }),
    summary: SeedAssetSummarySchema,
  })
  .openapi('SeedAssetVerifyResponse');

export type SeedAssetVerifyResponse = z.infer<typeof SeedAssetVerifyResponseSchema>;
